import {
  normalizeHumanReviewResourceId,
  normalizeHumanReviewView,
} from "@/review/project-annotations-table"

import { SUPPORT_MESSAGE_MAX_CHARS } from "../model/constants"
import type { ParseActionArgsResult, SofiaProposedAction } from "../model/types"
import {
  parseRenderVisualizationArgs,
  readString,
} from "./actionArgumentReaders"
import {
  isNavigateDestination,
  isNavigateDetailDestination,
  isNavigateListDestination,
} from "./actionRoutes"

export function parseNavigationActionArgs(
  action: SofiaProposedAction,
): ParseActionArgsResult | undefined {
  const problems: string[] = []
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- This parser owns only browser-side actions.
  switch (action.kind) {
    case "navigate": {
      const allowed = new Set([
        "destination",
        "resource_id",
        "human_review_view",
      ])
      if (
        Object.entries(action.args).some(
          ([key, value]) => !allowed.has(key) || value === null,
        )
      ) {
        return { ok: false, problems: ["invalid navigate arguments"] }
      }
      const destination = readString(action.args, "destination")
      const resource_id = readString(action.args, "resource_id")
      let human_review_view
      // Mirror sofia-agent's `_validate_navigate_args`: unknown
      // destination, missing id for a detail page, and id on a list page all
      // fail closed.
      if (!destination || !isNavigateDestination(destination)) {
        return { ok: false, problems: ["invalid destination"] }
      }
      if (isNavigateDetailDestination(destination) && !resource_id) {
        problems.push(`resource_id is required for navigate to ${destination}`)
      }
      if (isNavigateListDestination(destination) && resource_id) {
        problems.push(
          `resource_id is not allowed for navigate to ${destination}`,
        )
      }
      if ("human_review_view" in action.args) {
        if (destination !== "human_review_project" || !resource_id) {
          problems.push(
            "human_review_view is only allowed for human_review_project",
          )
        } else {
          try {
            normalizeHumanReviewResourceId(resource_id)
            human_review_view = normalizeHumanReviewView(
              action.args.human_review_view,
            )
          } catch (error) {
            problems.push(
              error instanceof Error
                ? error.message
                : "invalid human_review_view",
            )
          }
        }
      }
      if (problems.length > 0) {
        return { ok: false, problems }
      }
      return {
        ok: true,
        action: {
          kind: "navigate",
          args: {
            destination,
            resource_id,
            ...(human_review_view ? { human_review_view } : {}),
          },
        },
      }
    }
    case "contact_support": {
      const message = readString(action.args, "message")?.trim()
      if (
        Object.keys(action.args).some((key) => key !== "message") ||
        !message ||
        message.length > SUPPORT_MESSAGE_MAX_CHARS
      ) {
        return { ok: false, problems: ["invalid support message"] }
      }
      return {
        ok: true,
        action: { kind: "contact_support", args: { message } },
      }
    }
    case "render_visualization": {
      const parsed = parseRenderVisualizationArgs(action.args)
      if (!parsed) {
        return { ok: false, problems: ["invalid chart spec"] }
      }
      return {
        ok: true,
        action: { kind: "render_visualization", args: parsed },
      }
    }
    default:
      return undefined
  }
}
